import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { ISegmentedBarSegment, SegmentedBarComponent } from 'src/app/ui/segmented-bar/segmented-bar.component';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { ILineComposition } from '../../models/quotations.models';

/** One legend row: coloured dot, label, count and share of the total. */
interface ILegendRow {
  tone: 'success' | 'alert' | 'destructive';
  labelKey: string;
  count: number;
  percent: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// LineCompositionPopoverComponent
// ─────────────────────────────────────────────────────────────────────────────
// Breakdown of an order's lines (WB-8066). Exposes a <mat-menu> via @ViewChild so
// any surface can wire `[matMenuTriggerFor]="popover.menu"` onto its own trigger —
// the orders list cell, the order detail header and the side-by-side panel all
// share this one component so the three can never disagree.
//
// Purely presentational: it takes the backend's `line_composition` block and
// renders it. No fetching, no derivation beyond percentages.
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-line-composition-popover',
  standalone: true,
  imports: [CommonModule, MatMenuModule, TranslateModule, SegmentedBarComponent, SvgIconComponent],
  templateUrl: './line-composition-popover.component.html',
  styleUrls: ['./line-composition-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineCompositionPopoverComponent {
  @Input() composition?: ILineComposition | null;

  /** Order reference shown top-right (e.g. "QUO-2050"). Optional. */
  @Input() orderName = '';

  /** Exposed so the parent can bind [matMenuTriggerFor]. */
  @ViewChild('compositionMenu', { static: true }) menu!: MatMenu;

  get totalLines(): number {
    return this.composition?.total_lines ?? 0;
  }

  get segments(): ISegmentedBarSegment[] {
    const c = this.composition;
    if (!c) return [];
    return [
      { value: c.matched_lines, tone: 'success' },
      { value: c.lines_needing_review, tone: 'alert' },
      { value: c.unmatched_lines, tone: 'destructive' },
    ];
  }

  /**
   * Rows are always all four, including zeros: a stable legend lets the eye
   * compare orders down the list without the rows shifting position. (The bar
   * itself does drop zero segments — a 0%-wide band would render as a seam.)
   *
   * "Matched" and "Manually added" are both `success` tone and both drawn
   * from `matched_lines` (manually_added_lines is a subset of it) -- they
   * render as one contiguous green band on the bar above, split into two
   * rows here only so the count of hand-typed lines is visible on its own.
   */
  get legend(): ILegendRow[] {
    const c = this.composition;
    if (!c) return [];
    const aiMatched = c.matched_lines - c.manually_added_lines;
    return [
      { tone: 'success', labelKey: 'quotations_and_orders.line_composition.matched', count: aiMatched, percent: this.share(aiMatched) },
      { tone: 'success', labelKey: 'quotations_and_orders.line_composition.manually_added', count: c.manually_added_lines, percent: this.share(c.manually_added_lines) },
      { tone: 'alert', labelKey: 'quotations_and_orders.line_composition.need_review', count: c.lines_needing_review, percent: this.share(c.lines_needing_review) },
      { tone: 'destructive', labelKey: 'quotations_and_orders.line_composition.unmatched', count: c.unmatched_lines, percent: this.share(c.unmatched_lines) },
    ];
  }

  private share(value: number): number {
    const total = this.totalLines;
    return total > 0 ? Math.round((value / total) * 100) : 0;
  }
}
